<template>
  <div class="export-modal-overlay" @click.self="handleClose">
    <div class="export-modal">
      <div class="export-modal-header">
        <h3>{{ headerText }}</h3>
        <button v-if="canClose" class="close-btn" @click="handleClose">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <div class="export-modal-content">
        <div v-if="status === 'rendering'" class="status-section">
          <div class="progress-icon rendering">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"/>
            </svg>
          </div>
          <p class="status-text">{{ $t('Генерация изображения...') }}</p>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: renderProgress + '%' }"></div>
          </div>
          <span class="progress-percent">{{ renderProgress }}%</span>
        </div>
        
        <div v-else-if="status === 'uploading'" class="status-section">
          <div class="progress-icon uploading">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
            </svg>
          </div>
          <p class="status-text">{{ $t('Сохранение на сервер...') }}</p>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <span class="progress-percent">{{ uploadProgress }}%</span>
        </div>
        
        <div v-else-if="status === 'ready'" class="status-section ready">
          <div class="progress-icon success">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <p class="status-text">{{ $t('Изображение готово!') }}</p>
          <button class="open-btn" @click="openImage">
            {{ $t('legacy.Открыть') }}
          </button>
        </div>
        
        <div v-else-if="status === 'error'" class="status-section error">
          <div class="progress-icon error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M15 9l-6 6M9 9l6 6"/>
            </svg>
          </div>
          <p class="status-text">{{ $t('legacy.Ошибка при создании') }}</p>
          <p class="error-message">{{ errorMessage }}</p>
          <button class="retry-btn" @click="$emit('retry')">
            {{ $t('legacy.Попробовать снова') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExportModal',
  props: {
    status: {
      type: String,
      default: 'rendering',
      validator: (val) => ['rendering', 'uploading', 'ready', 'error'].includes(val)
    },
    renderProgress: {
      type: Number,
      default: 0
    },
    uploadProgress: {
      type: Number,
      default: 0
    },
    downloadUrl: {
      type: String,
      default: ''
    },
    errorMessage: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'retry'],
  computed: {
    headerText() {
      switch (this.status) {
        case 'rendering': return this.$t('legacy.Создание изображения');
        case 'uploading': return this.$t('common.save');
        case 'ready': return `${this.$t('common.success')}!`;
        case 'error': return this.$t('common.error');
        default: return this.$t('legacy.Экспорт');
      }
    },
    canClose() {
      return this.status === 'ready' || this.status === 'error';
    }
  },
  methods: {
    handleClose() {
      if (this.canClose) {
        this.$emit('close');
      }
    },
    openImage() {
      if (this.downloadUrl) {
        window.open(this.downloadUrl, '_blank');
        this.$emit('close');
      }
    }
  }
};
</script>

<style scoped lang="scss">
.export-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.export-modal {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 340px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.export-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }
  
  .close-btn {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    
    &:hover {
      background: #f0f0f0;
    }
  }
}

.export-modal-content {
  padding: 30px 20px;
}

.status-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.progress-icon {
  margin-bottom: 16px;
  
  &.rendering svg,
  &.uploading svg {
    animation: spin 1.5s linear infinite;
    color: #496782;
  }
  
  &.success svg {
    color: #22c55e;
  }
  
  &.error-icon svg {
    color: #ef4444;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.status-text {
  font-size: 16px;
  color: #333;
  margin: 0 0 20px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #496782, #5a7a99);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-percent {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.open-btn {
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #496782, #5a7a99);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:active {
    transform: scale(0.98);
  }
}

.retry-btn {
  width: 100%;
  padding: 14px 24px;
  background: #f3f4f6;
  color: #333;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 10px;
}

.error-message {
  font-size: 14px;
  color: #666;
  margin: 0 0 16px;
}

.ready .status-text {
  color: #22c55e;
  font-weight: 500;
}
</style>
